import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import DoorBrandLockup from "@/components/DoorBrandLockup";
import { doorPath, useOrgDoor, type DoorOrg } from "@/hooks/useOrgDoor";
import type { User } from "@/types";
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Phone,
  ShieldCheck,
  User as UserIcon,
  Users,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useAuth, WrongOrgDoorError } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { TierSelector, PENDING_STRIPE_PAYMENT_KEY, readPendingStripePayment } from "@/components/TierSelector";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { PASSWORD_MIN_LENGTH, firstPasswordIssue } from "@/lib/password";
import { preloadRecaptcha } from "@/lib/recaptcha";
import { safeRedirectPath } from "@/lib/roleHome";
import { CaptchaField, useCaptcha } from "@/components/Captcha";

type Role = "buyer" | "seller" | "bl_advisor";

type BuyerRegistrationData = {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  legal_consent: { consent_accepted: boolean };
  referral_code?: string;
  // Solved captcha carried across the payment step (challenges outlive it by design).
  captcha_token?: string;
  captcha_answer?: string;
};

// Registration form data stashed while the buyer is in the payment step, so a
// redirect-based payment method (which unloads the page) can still finish
// account creation on return. Tab-scoped (sessionStorage) and removed as soon
// as the account exists.
const PENDING_REGISTRATION_KEY = "bl_pending_buyer_registration";

const ROLE_INFO: Partial<Record<Role, { label: string; desc: string; icon: ReactNode }>> = {
  buyer: {
    label: "Buyer",
    desc: "Discover opportunities",
    icon: <ShieldCheck className="h-4 w-4" />,
  },
  // seller: {
  //   label: "Seller",
  //   desc: "Publish listings",
  //   icon: <Store className="h-4 w-4" />,
  // },
  bl_advisor: {
    label: "BL Advisors",
    desc: "Support transactions",
    icon: <Briefcase className="h-4 w-4" />,
  },
};

function RoleOption({
  roleKey,
  active,
  onClick,
}: {
  roleKey: Role;
  active: boolean;
  onClick: (role: Role) => void;
}) {
  const info = ROLE_INFO[roleKey];
  if (!info) return null;

  return (
    <button
      type="button"
      onClick={() => onClick(roleKey)}
      className={`relative rounded-xl border px-3 py-3 text-left transition-colors duration-200 ${
        active
          ? "border-primary/60 bg-primary/10 text-foreground"
          : "border-border/70 bg-muted/20 text-muted-foreground hover:border-primary/30 hover:bg-muted/30"
      }`}
    >
      {active && <CheckCircle2 className="absolute right-2.5 top-2.5 h-4 w-4 text-primary" />}
      <div className="inline-flex items-center gap-2">
        <span className={active ? "text-primary" : ""}>{info.icon}</span>
        <span className="text-sm font-semibold">{info.label}</span>
      </div>
      <div className="mt-1 text-xs">{info.desc}</div>
    </button>
  );
}

// Compact brand lockup shown above the form on phones/tablets, where the full
// decorative BrandAside is hidden (< lg). Keeps the marketplace branding without
// the long scroll of the full panel.
function MobileBrandHeader({ org }: { org: DoorOrg | null }) {
  return (
    <div className="lg:hidden">
      <DoorBrandLockup org={org} className="h-10 sm:h-12" orgClassName="h-12 sm:h-14" />
      <h1 className="mt-4 font-display text-2xl font-bold leading-[1.15] text-foreground sm:text-3xl">
        Access your <span className="text-primary">marketplace</span> workspace.
      </h1>
      <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
        Sign in or create an account to browse listings, manage your profile, and run your buyer or advisor workflow.
      </p>
    </div>
  );
}

function BrandAside({ org }: { org: DoorOrg | null }) {
  return (
    <aside className="public-auth-aside relative hidden overflow-hidden lg:flex lg:min-h-screen lg:flex-col">
      <div className="relative z-10 flex h-full flex-col px-10 pb-12 pt-16 xl:px-14">
        <DoorBrandLockup org={org} className="h-16" orgClassName="h-16" />

        <h1 className="mt-9 font-display text-4xl font-bold leading-[1.08] text-foreground xl:text-[2.9rem]">
          Access your <span className="text-primary">marketplace</span>
          <br />
          workspace.
        </h1>

        <p className="mt-5 max-w-md text-[0.95rem] leading-7 text-muted-foreground">
          Sign in or create an account to browse listings, manage your profile, and run your buyer or seller workflow.
        </p>

        <div className="mt-10 space-y-5">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold text-foreground">One account. Everything you need.</div>
              <div className="mt-0.5 text-xs leading-6 text-muted-foreground">Buyer and seller routes in one secure system.</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold text-foreground">Role-based access.</div>
              <div className="mt-0.5 text-xs leading-6 text-muted-foreground">Designed for sellers, BL Advisors, and admins.</div>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <div className="flex items-center justify-between rounded-xl border border-border/70 bg-muted/20 px-4 py-3.5">
            <div>
              <div className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Secure &amp; Trusted Access</div>
              <div className="mt-1 text-sm font-bold text-foreground">OAuth + Email Authentication</div>
            </div>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-primary">
              <Lock className="h-4 w-4" />
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}

const EMAIL_REGEX = /^[^\s@+][^\s@]*@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value: string) => EMAIL_REGEX.test(value.trim());

export default function AuthPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { slug: orgSlug, org } = useOrgDoor();
  // Set when the credentials were right but presented at another
  // organization's door — we show a link rather than a dead end.
  const [wrongDoor, setWrongDoor] = useState<{ message: string; url: string } | null>(null);
  // Match on the LAST segment: this page also serves an organization's doors at
  // /<slug>/sign-in and /<slug>/register, where an exact "/register" test would
  // wrongly render the sign-in form.
  const isLogin = !location.pathname.endsWith("/register");
  // Every in-page hop has to stay inside the organization's door — leaving it
  // would drop the visitor onto the main site's signup.
  const signInPath = doorPath("/sign-in", orgSlug);
  const registerPath = doorPath("/register", orgSlug);

  // BL Advisor buyer referral: ?ref=CODE on the registration page turns this
  // into a buyer signup attributed to that advisor.
  const referralCode = (!isLogin && searchParams.get("ref")?.trim()) || "";
  const referralMode = Boolean(referralCode);
  const [referralAdvisor, setReferralAdvisor] = useState<string | null>(null);
  const [referralChecked, setReferralChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<Role>("buyer");
  const [advisorCode, setAdvisorCode] = useState("");
  const [advisorCodeVerified, setAdvisorCodeVerified] = useState(false);
  const [advisorCodeLoading, setAdvisorCodeLoading] = useState(false);
  const [advisorCodeError, setAdvisorCodeError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [legalLoading, setLegalLoading] = useState(false);
  const [legalDocs, setLegalDocs] = useState<{ terms_text: string; terms_version: string; nda_version: string } | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);

  const [socialPending, setSocialPending] = useState<{ provider: "google" | "facebook"; token: string } | null>(null);
  const [socialRole, setSocialRole] = useState<Role>("buyer");
  const [socialAdvisorCode, setSocialAdvisorCode] = useState("");
  const [socialAdvisorCodeVerified, setSocialAdvisorCodeVerified] = useState(false);
  const [socialAdvisorCodeLoading, setSocialAdvisorCodeLoading] = useState(false);
  const [socialAdvisorCodeError, setSocialAdvisorCodeError] = useState("");
  const [socialConsentAccepted, setSocialConsentAccepted] = useState(false);

  // Tier selection for buyer signup
  const [showTierSelector, setShowTierSelector] = useState(false);
  const [registrationData, setRegistrationData] = useState<BuyerRegistrationData | null>(null);
  // Built-in image captcha — active only while reCAPTCHA Enterprise is unconfigured.
  const captcha = useCaptcha();

  // Inline OTP verification state
  const [emailVerified, setEmailVerified] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // The Google button renders a fixed-pixel iframe. Measure its container so it
  // scales down on narrow screens instead of overflowing/clipping (Google caps
  // the rendered width at 400px).
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(360);

  // Terms consent gates social sign-up: on the register page a user must accept
  // the T&C before Google/Facebook are usable. The checkbox lives lower in the
  // form, so a blocked click scrolls to and highlights it.
  const consentRef = useRef<HTMLDivElement>(null);
  const [consentHighlight, setConsentHighlight] = useState(false);

  // Forgot-password flow (sign-in page only)
  type FpStep = "idle" | "enter-email" | "enter-otp" | "enter-password" | "done";
  const [fpStep, setFpStep] = useState<FpStep>("idle");
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPassword, setFpNewPassword] = useState("");
  const [fpConfirmPassword, setFpConfirmPassword] = useState("");
  const [fpShowPassword, setFpShowPassword] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState("");
  const [fpCooldown, setFpCooldown] = useState(0);
  const fpCooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startFpCooldown = () => {
    setFpCooldown(60);
    if (fpCooldownRef.current) clearInterval(fpCooldownRef.current);
    fpCooldownRef.current = setInterval(() => {
      setFpCooldown((prev) => {
        if (prev <= 1) { clearInterval(fpCooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleFpSendOtp = async (emailOverride?: string) => {
    const target = (emailOverride ?? fpEmail).trim();
    if (!target) return;
    setFpLoading(true);
    setFpError("");
    try {
      await api.post("/auth/forgot-password/send-otp", { email: target });
      setFpStep("enter-otp");
      setFpOtp("");
      startFpCooldown();
    } catch (err: unknown) {
      setFpError(err instanceof Error ? err.message : "Failed to send code");
    } finally {
      setFpLoading(false);
    }
  };

  const handleFpReset = async (event: React.FormEvent) => {
    event.preventDefault();
    const fpIssue = firstPasswordIssue(fpNewPassword);
    if (fpIssue) { setFpError(fpIssue); return; }
    if (fpNewPassword !== fpConfirmPassword) { setFpError("Passwords do not match"); return; }
    setFpLoading(true);
    setFpError("");
    try {
      await api.post("/auth/forgot-password/reset", {
        email: fpEmail.trim(),
        otp_code: fpOtp.trim(),
        new_password: fpNewPassword,
      });
      setFpStep("done");
    } catch (err: unknown) {
      setFpError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setFpLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setFpStep("idle");
    setFpEmail("");
    setFpOtp("");
    setFpNewPassword("");
    setFpConfirmPassword("");
    setFpError("");
    setFpCooldown(0);
    if (fpCooldownRef.current) clearInterval(fpCooldownRef.current);
  };

  const navigate = useNavigate();
  const { login, register, registerBuyer, socialLogin, getLegalDocuments, sendOtp, verifyOtp, verifyAdvisorCode } = useAuth();
  const toast = useToast();

  /**
   * Where a returning user lands after signing in: the page they were sent to,
   * when a guard bounced them here with `?next=` (an emailed "sign the NDA"
   * link, a deal link, a notification), otherwise their role home. Registration
   * ignores it — a brand-new account belongs on its own home first.
   */
  function postLoginPath(user: User): string {
    const next = safeRedirectPath(searchParams.get("next"));
    // Buyers always start on their dashboard. A `next` that merely points back
    // into their own workspace (an expired session on /buyer/home bounces here
    // with ?next=/buyer/home) must not override that; real action links — the
    // NDA, a deal, a chat thread (/buyer/interests?thread=…) — still resume.
    if (next && user?.roles?.includes("buyer") && isPlainBuyerPage(next)) {
      return getRoleHomePath(user);
    }
    return next ?? getRoleHomePath(user);
  }

  function isPlainBuyerPage(path: string): boolean {
    // Optional org prefix (/ecfa/buyer/...), no query string.
    return !path.includes("?") && /^(\/[^/]+)?\/buyer\//.test(path);
  }

  function getRoleHomePath(user: User): string {
    const roles = user?.roles ?? [];
    if (roles.includes("admin")) return "/admin/home";
    if (roles.includes("buyer")) {
      // The dashboard, matching homePathForUser() in lib/roleHome.ts — keep the
      // two in step. Free (Basic) buyers land here too; each card degrades to
      // its own empty or upgrade state rather than gating the whole page.
      return "/buyer/dashboard";
    }
    if (roles.includes("seller")) return "/seller/home";
    if (roles.includes("bl_advisor")) return "/bl-advisor/home";
    return "/";
  }

  // ── Resume signup after a redirect-based Stripe payment ──────────────────
  // stripe.confirmPayment can leave the page entirely (bank redirects etc.),
  // wiping all React state mid-signup. The form data and the paid subscription
  // were stashed in sessionStorage before the redirect; Stripe returns the
  // browser here with ?redirect_status=…. Finish creating the account for the
  // ALREADY-PAID subscription — reloading this URL retries safely, since the
  // stored subscription id is reused and nothing is charged again.
  // Warm up reCAPTCHA on mount so its v3 badge shows on the auth page rather
  // than only appearing after the first submit. No-op without a site key.
  useEffect(() => { preloadRecaptcha(); }, []);

  const resumeAttemptedRef = useRef(false);
  useEffect(() => {
    if (isLogin || resumeAttemptedRef.current) return;
    const redirectStatus = searchParams.get("redirect_status");
    if (!redirectStatus) return;
    const pendingPayment = readPendingStripePayment();
    const rawReg = sessionStorage.getItem(PENDING_REGISTRATION_KEY);
    if (!pendingPayment || !rawReg) return;
    let reg: BuyerRegistrationData;
    try { reg = JSON.parse(rawReg); } catch { return; }
    resumeAttemptedRef.current = true;

    if (redirectStatus !== "succeeded" && redirectStatus !== "processing") {
      // Payment never completed — drop the stale payment stash and reopen the
      // plan step with the buyer's details restored.
      sessionStorage.removeItem(PENDING_STRIPE_PAYMENT_KEY);
      setRegistrationData(reg);
      setShowTierSelector(true);
      toast.error("Payment was not completed. Please try again.");
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const buyerUser = await registerBuyer({
          ...reg,
          tier_id: pendingPayment.tierId,
          stripe_subscription_id: pendingPayment.subscriptionId,
          referral_code: reg.referral_code,
        }, { captcha_token: reg.captcha_token, captcha_answer: reg.captcha_answer });
        sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
        sessionStorage.removeItem(PENDING_STRIPE_PAYMENT_KEY);
        toast.success("Account created successfully!");
        navigate(getRoleHomePath(buyerUser));
      } catch (error: unknown) {
        // Keep both stashes: refreshing retries account creation for the same
        // paid subscription. If it never completes, the daily reconcile sweep
        // cancels and refunds the orphaned subscription.
        toast.error(
          error instanceof Error
            ? error.message
            : "Your payment succeeded but account creation failed. Refresh to retry, or contact support.",
        );
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";
  const facebookAppId = import.meta.env.VITE_FACEBOOK_APP_ID || "";

  useEffect(() => {
    if (!facebookAppId) return;
    if (document.getElementById("facebook-jssdk")) return;

    (window as any).fbAsyncInit = () => {
      (window as any).FB.init({ appId: facebookAppId, cookie: true, xfbml: false, version: "v19.0" });
    };

    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    document.body.appendChild(script);
  }, [facebookAppId]);

  // Keep the Google button sized to its container across resizes/breakpoints.
  useEffect(() => {
    const el = googleBtnRef.current;
    if (!el) return;
    const measure = () => setGoogleBtnWidth(Math.min(400, Math.max(200, Math.floor(el.clientWidth))));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [googleClientId, facebookAppId, referralMode, isLogin]);

  // Show a notice when we landed here because the session timed out (redirect
  // set by the api layer on a 401 after the inactivity window elapsed).
  useEffect(() => {
    if (sessionStorage.getItem("session_expired")) {
      sessionStorage.removeItem("session_expired");
      toast.error("Your session expired after inactivity. Please log in again.");
    }
  }, [toast]);

  useEffect(() => {
    let cancelled = false;
    setLegalLoading(true);
    getLegalDocuments()
      .then((docs) => {
        if (!cancelled) setLegalDocs(docs);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : "Unable to load legal documents");
        }
      })
      .finally(() => {
        if (!cancelled) setLegalLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getLegalDocuments, toast]);

  // Resolve the referral code to the advisor's name for a confirmation banner.
  useEffect(() => {
    if (!referralMode) {
      setReferralAdvisor(null);
      setReferralChecked(false);
      return;
    }
    let cancelled = false;
    setReferralChecked(false);
    api
      .get<{ valid: boolean; advisor_name: string | null }>(`/referrals/validate/${encodeURIComponent(referralCode)}`)
      .then((res) => {
        if (!cancelled) setReferralAdvisor(res.valid ? res.advisor_name : null);
      })
      .catch(() => {
        if (!cancelled) setReferralAdvisor(null);
      })
      .finally(() => {
        if (!cancelled) setReferralChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [referralMode, referralCode]);

  const startResendCooldown = () => {
    setOtpResendCooldown(60);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setOtpResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetEmailVerification = () => {
    setEmailVerified(false);
    setShowOtpInput(false);
    setOtpCode("");
    setOtpError("");
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setOtpResendCooldown(0);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailAlreadyExists(false);
    setOtpError("");
    if (emailVerified || showOtpInput) resetEmailVerification();
  };

  const handleSendOtp = async () => {
    if (!email) return;
    if (!isValidEmail(email)) {
      setOtpError("Please enter a valid email address.");
      return;
    }
    setOtpLoading(true);
    setOtpError("");
    setEmailAlreadyExists(false);
    try {
      await sendOtp(email, captcha.payload());
      setShowOtpInput(true);
      setOtpCode("");
      startResendCooldown();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Failed to send code";
      if (msg.toLowerCase().includes("already exists")) {
        setEmailAlreadyExists(true);
      } else {
        setOtpError(msg);
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleConfirmOtp = async () => {
    if (otpCode.length !== 6) return;
    setOtpLoading(true);
    setOtpError("");
    try {
      await verifyOtp(email, otpCode);
      setEmailVerified(true);
      setShowOtpInput(false);
      setOtpCode("");
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      setOtpResendCooldown(0);
    } catch (error: unknown) {
      setOtpError(error instanceof Error ? error.message : "Incorrect OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpLoading(true);
    setOtpError("");
    try {
      await sendOtp(email, captcha.payload());
      setOtpCode("");
      startResendCooldown();
    } catch (error: unknown) {
      setOtpError(error instanceof Error ? error.message : "Failed to resend code");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSocialRoleChange = (next: Role) => {
    setSocialRole(next);
    if (next !== "bl_advisor") {
      setSocialAdvisorCodeVerified(false);
      setSocialAdvisorCodeError("");
    }
  };

  const handleSocialAdvisorCodeChange = (value: string) => {
    setSocialAdvisorCode(value);
    setSocialAdvisorCodeError("");
    if (socialAdvisorCodeVerified) setSocialAdvisorCodeVerified(false);
  };

  const handleVerifySocialAdvisorCode = async () => {
    if (!socialAdvisorCode.trim()) return;
    setSocialAdvisorCodeLoading(true);
    setSocialAdvisorCodeError("");
    try {
      await verifyAdvisorCode(socialAdvisorCode.trim(), captcha.payload());
      setSocialAdvisorCodeVerified(true);
    } catch (error: unknown) {
      setSocialAdvisorCodeVerified(false);
      setSocialAdvisorCodeError(error instanceof Error ? error.message : "Invalid BL Advisor access code");
    } finally {
      setSocialAdvisorCodeLoading(false);
    }
  };

  const handleRoleChange = (next: Role) => {
    setRole(next);
    if (next !== "bl_advisor") {
      setAdvisorCodeVerified(false);
      setAdvisorCodeError("");
    }
  };

  const handleAdvisorCodeChange = (value: string) => {
    setAdvisorCode(value);
    setAdvisorCodeError("");
    if (advisorCodeVerified) setAdvisorCodeVerified(false);
  };

  const handleVerifyAdvisorCode = async () => {
    if (!advisorCode.trim()) return;
    setAdvisorCodeLoading(true);
    setAdvisorCodeError("");
    try {
      await verifyAdvisorCode(advisorCode.trim(), captcha.payload());
      setAdvisorCodeVerified(true);
    } catch (error: unknown) {
      setAdvisorCodeVerified(false);
      setAdvisorCodeError(error instanceof Error ? error.message : "Invalid BL Advisor access code");
    } finally {
      setAdvisorCodeLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      if (isLogin) {
        setLoading(true);
        const loggedInUser = await login(email, password, captcha.payload());
        const warningRaw = sessionStorage.getItem("login_expiry_warning");
        if (warningRaw) {
          try {
            const warning = JSON.parse(warningRaw) as { expiry_days_remaining: number | null; account_expiry_date: string | null };
            if (warning.expiry_days_remaining !== null && warning.expiry_days_remaining <= 3) {
              if (warning.expiry_days_remaining < 0) {
                toast.error("Your account has expired. Please make your monthly payment from Profile.");
              } else if (warning.expiry_days_remaining === 0) {
                toast.error("Your account expires today. Please make your monthly payment from Profile.");
              } else {
                toast.error(`Your account expires in ${warning.expiry_days_remaining} day(s). Please make your monthly payment from Profile.`);
              }
            }
          } catch {
            // Ignore parsing errors and continue login flow.
          } finally {
            sessionStorage.removeItem("login_expiry_warning");
          }
        }
        navigate(postLoginPath(loggedInUser));
      } else {
        if (!consentAccepted) throw new Error("You must accept the consent checkbox before creating an account");
        if (!emailVerified) throw new Error("Please verify your email before continuing");
        // Enforced here so a weak password can never reach the payment step —
        // the backend register endpoints apply the same rules.
        const passwordIssue = firstPasswordIssue(password);
        if (passwordIssue) throw new Error(passwordIssue);
        if (!referralMode && role === "bl_advisor" && !advisorCodeVerified) throw new Error("Please verify the BL Advisor access code before continuing");

        const regData = {
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          phone: phone || undefined,
          legal_consent: { consent_accepted: true },
        };

        // A referral link creates an ordinary free Basic buyer — no plan step,
        // no payment. The code rides along so the backend can attribute them to
        // the advisor; upgrading happens later on /choose-plan.
        const signupRole: Role = referralMode ? "buyer" : role;

        setLoading(true);
        const registeredUser = await register({
          ...regData,
          role: signupRole,
          advisor_code: signupRole === "bl_advisor" ? advisorCode.trim() : undefined,
          referral_code: referralCode || undefined,
        }, captcha.payload());
        navigate(getRoleHomePath(registeredUser));
      }
    } catch (error: unknown) {
      // Challenges are consumed on use — hand back a fresh one after a failure.
      captcha.refresh();
      if (error instanceof WrongOrgDoorError) {
        // Keep ?next= across the door switch: an emailed link (e.g. "Review & sign
        // the NDA") opened at the wrong door must still land on its page after
        // sign-in, not on the dashboard (TKT-00159, an ECFA buyer).
        const next = searchParams.get("next");
        setWrongDoor({
          message: error.message,
          url: next ? `${error.signInUrl}?next=${encodeURIComponent(next)}` : error.signInUrl,
        });
      } else {
        toast.error(error instanceof Error ? error.message : "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTierSelected = async (selection: { tierId: number; subscriptionId?: string; promoCode?: string }) => {
    if (!registrationData) {
      toast.error("Registration details are missing. Please try again.");
      return;
    }

    try {
      setLoading(true);
      const buyerUser = await registerBuyer({
        ...registrationData,
        tier_id: selection.tierId,
        stripe_subscription_id: selection.subscriptionId,
        promo_code: selection.promoCode,
        referral_code: registrationData.referral_code,
      }, {
        captcha_token: registrationData.captcha_token,
        captcha_answer: registrationData.captcha_answer,
      });

      // Account exists — the redirect-resume stashes are no longer needed.
      sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
      sessionStorage.removeItem(PENDING_STRIPE_PAYMENT_KEY);
      toast.success("Account created successfully!");
      navigate(getRoleHomePath(buyerUser));
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialToken = async (
    provider: "google" | "facebook",
    token: string,
    selectedRole?: string,
    legalConsent?: { consent_accepted: boolean },
    advisorCodeValue?: string,
  ) => {
    setLoading(true);

    try {
      const result = await socialLogin(provider, token, selectedRole, legalConsent, advisorCodeValue);
      if (result === "role_required") {
        setSocialPending({ provider, token });
        return;
      }
      navigate(postLoginPath(result));
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Social login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRoleSubmit = async () => {
    if (!socialPending) return;
    if (!socialConsentAccepted) {
      toast.error("You must accept consent before creating an account");
      return;
    }
    if (socialRole === "bl_advisor" && !socialAdvisorCodeVerified) {
      toast.error("Please verify the BL Advisor access code before continuing");
      return;
    }
    await handleSocialToken(
      socialPending.provider,
      socialPending.token,
      socialRole,
      { consent_accepted: true },
      socialRole === "bl_advisor" ? socialAdvisorCode.trim() : undefined,
    );
  };

  const handleFacebookLogin = () => {
    const FB = (window as any).FB;
    if (!FB) {
      toast.error("Facebook SDK not loaded. Please try again.");
      return;
    }

    FB.login(
      (response: any) => {
        if (response.authResponse?.accessToken) {
          handleSocialToken("facebook", response.authResponse.accessToken);
        }
      },
      { scope: "email,public_profile" },
    );
  };

  // Register mode only: block Google/Facebook until the Terms checkbox is ticked.
  const socialGateBlocked = !isLogin && !consentAccepted;
  const handleSocialGateBlocked = () => {
    toast.error("Please accept the Terms and Conditions to continue.");
    setConsentHighlight(true);
    consentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (socialPending) {
    return (
      <div data-theme="dark" className="public-stage min-h-screen px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-xl">
          <Card className="public-auth-card rounded-[1.6rem]">
            <CardHeader>
              <CardTitle className="text-2xl font-bold tracking-tight">Select account role</CardTitle>
              <CardDescription>
                Choose your role to complete social sign-in.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5">
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(ROLE_INFO) as Role[]).map((roleKey) => (
                  <RoleOption key={roleKey} roleKey={roleKey} active={socialRole === roleKey} onClick={handleSocialRoleChange} />
                ))}
              </div>

              {socialRole === "bl_advisor" && (
                <div className="space-y-1.5">
                  <Label htmlFor="socialAdvisorCode">BL Advisor access code <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                    <Input
                      id="socialAdvisorCode"
                      placeholder="Enter access code"
                      value={socialAdvisorCode}
                      onChange={(event) => handleSocialAdvisorCodeChange(event.target.value)}
                      className="pl-10 pr-24"
                      required
                      readOnly={socialAdvisorCodeVerified}
                    />
                    {socialAdvisorCodeVerified ? (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                        <CheckCircle2 className="h-4 w-4" />
                        Verified
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleVerifySocialAdvisorCode}
                        disabled={socialAdvisorCodeLoading || !socialAdvisorCode.trim()}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-gold-gradient px-2.5 py-1 text-xs font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
                      >
                        {socialAdvisorCodeLoading ? "Verifying…" : "Verify"}
                      </button>
                    )}
                  </div>
                  {socialAdvisorCodeError ? (
                    <p className="text-xs font-medium text-destructive">{socialAdvisorCodeError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Required to create a BL Advisor account.</p>
                  )}
                </div>
              )}

              <div className="space-y-3 rounded-xl border border-border/75 bg-muted/25 p-3">
                <div className="text-sm text-muted-foreground">
                  Read the latest terms and privacy policy in a new window before continuing.
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button asChild variant="outline" className="border-primary/20 bg-white/72 hover:bg-white">
                    <a href="https://www.businesslocating.com/terms" target="_blank" rel="noopener noreferrer">Open Terms and Conditions</a>
                  </Button>
                  <Button asChild variant="outline" className="border-primary/20 bg-white/72 hover:bg-white">
                    <a href="https://www.businesslocating.com/privacy" target="_blank" rel="noopener noreferrer">Open Privacy Policy</a>
                  </Button>
                </div>

                <label className="flex items-start gap-2.5 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-border text-primary"
                    checked={socialConsentAccepted}
                    onChange={(event) => setSocialConsentAccepted(event.target.checked)}
                  />
                  <span>
                    I have read and agree to the <a href="https://www.businesslocating.com/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "#2563eb", textDecoration: "underline" }}>Terms and Conditions</a> and <a href="https://www.businesslocating.com/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "#2563eb", textDecoration: "underline" }}>Privacy Policy</a>, and consent to proceed.
                  </span>
                </label>

                {/* Buyers are exempt from the NDA — only warn seller/advisor signups. */}
                {socialRole !== "buyer" && (
                  <div className="space-y-1.5">
                    <Label>Note</Label>
                    <p className="text-sm text-muted-foreground">NDA signature will be required immediately after account creation.</p>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button className="flex-1 shadow-glow" onClick={handleSocialRoleSubmit} disabled={loading || legalLoading || !legalDocs || (socialRole === "bl_advisor" && !socialAdvisorCodeVerified)}>
                  {loading ? "Please wait..." : "Continue"}
                  {!loading && <ArrowRight className="h-4 w-4" />}
                </Button>
                <Button variant="outline" className="border-primary/20 bg-white/72 hover:bg-white" onClick={() => setSocialPending(null)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show tier selector for buyer signup
  if (showTierSelector && !isLogin) {
    return (
      <div data-theme="dark" className="public-stage min-h-screen px-4 py-8 sm:px-6 lg:px-10">
        <div className="mx-auto w-full max-w-[1400px]">
          <div className="mb-6 relative flex items-center justify-center">
            <Button
              variant="outline"
              className="absolute left-0 shrink-0 border-primary/20 bg-white/72 hover:bg-white"
              onClick={() => {
                setShowTierSelector(false);
                setRegistrationData(null);
                // Backing out of the payment step — drop the redirect-resume stash.
                sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
              }}
              disabled={loading}
            >
              ← Back to Registration
            </Button>
            <div className="text-center">
              <h2 className="text-2xl font-bold tracking-tight text-foreground">
                Choose Your Subscription Plan
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Select a plan to start accessing business opportunities.
              </p>
            </div>
          </div>

          <TierSelector
            registrationEmail={registrationData?.email || ""}
            onTierSelected={handleTierSelected}
            isLoading={loading}
            forRegistration
          />
        </div>
      </div>
    );
  }

  // Sign-in always renders in the Dark · Gold theme — the data-theme attribute
  // pins every palette variable to dark regardless of the app-wide toggle, and
  // no ThemeToggle is offered here by design.
  return (
    <div data-theme="dark" className="public-stage min-h-screen w-full lg:grid lg:grid-cols-[1.35fr_1fr]">
      <BrandAside org={org} />

      <div className="flex min-h-screen flex-col justify-center px-5 py-10 sm:px-8 lg:px-14 xl:px-20">
        <div className="mx-auto w-full max-w-[400px]">
          <MobileBrandHeader org={org} />
          <div className="mt-8 lg:mt-0">
            <div className="flex items-center gap-6 border-b border-border/60">
              <button
                type="button"
                onClick={() => navigate(signInPath)}
                className={`relative -mb-px flex items-center gap-2 pb-3 text-sm font-semibold transition-colors duration-200 ${
                  isLogin ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <UserIcon className="h-4 w-4" />
                Sign In
                {isLogin && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
              </button>
              <button
                type="button"
                onClick={() => navigate(registerPath)}
                className={`relative -mb-px flex items-center gap-2 pb-3 text-sm font-semibold transition-colors duration-200 ${
                  !isLogin ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Users className="h-4 w-4" />
                Create Account
                {!isLogin && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
              </button>
            </div>

            <div className="mt-6">
              <h2 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                {isLogin ? (
                  <>Welcome <span className="text-primary">back!</span></>
                ) : (
                  <>Create <span className="text-primary">your</span> account</>
                )}
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {isLogin
                  ? "Sign in to continue to your workspace."
                  : "Choose your role and complete the registration form."}
              </p>
            </div>

            {!referralMode && (googleClientId || facebookAppId) && (
              <div className="mt-6">
                <div className={`relative flex flex-col gap-2.5 ${socialGateBlocked ? "opacity-60" : ""}`}>
                  {socialGateBlocked && (
                    <div
                      className="absolute inset-0 z-10 cursor-not-allowed rounded-xl"
                      onClick={handleSocialGateBlocked}
                      role="button"
                      aria-label="Accept the Terms and Conditions to continue"
                      title="Please accept the Terms and Conditions to continue"
                    />
                  )}
                  {googleClientId && (
                    <div ref={googleBtnRef} className="flex justify-center overflow-hidden rounded-xl">
                      <GoogleLogin
                        onSuccess={(response) => {
                          if (response.credential) handleSocialToken("google", response.credential);
                        }}
                        onError={() => toast.error("Google login failed")}
                        theme="outline"
                        size="large"
                        shape="rectangular"
                        logo_alignment="center"
                        width={String(googleBtnWidth)}
                        text={isLogin ? "signin_with" : "signup_with"}
                      />
                    </div>
                  )}

                  {facebookAppId && (
                    <Button type="button" variant="outline" size="lg" className="w-full border-primary/20 bg-white/72 hover:bg-white" onClick={handleFacebookLogin} disabled={loading}>
                      Continue with Facebook
                    </Button>
                  )}
                </div>

                {socialGateBlocked && (
                  <p className="mt-2 text-center text-xs text-muted-foreground">
                    Tick the <span className="font-medium text-foreground">Terms and Conditions</span> below to continue with Google.
                  </p>
                )}

                <div className="relative mt-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border/60" />
                  </div>
                  <div className="relative flex justify-center text-[0.66rem] uppercase tracking-[0.16em]">
                    <span className="bg-background px-3 text-muted-foreground">Or continue with email</span>
                  </div>
                </div>
              </div>
            )}

            {wrongDoor && (
              <div className="mt-6 rounded-xl border border-primary/30 bg-primary/10 p-4">
                <p className="text-sm leading-6 text-foreground">{wrongDoor.message}</p>
                <Link
                  to={wrongDoor.url}
                  onClick={() => setWrongDoor(null)}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-gold-gradient px-4 py-2 text-sm font-semibold text-primary-foreground"
                >
                  Go to the right sign-in page
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              {!isLogin && (
                <>
                  {referralMode ? (
                    <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <Briefcase className="h-4 w-4 text-primary" />
                        Buyer account
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {referralAdvisor ? (
                          <>You were invited by <span className="font-semibold text-foreground">{referralAdvisor}</span>. Create your buyer account below.</>
                        ) : referralChecked ? (
                          "Create your buyer account below."
                        ) : (
                          "Verifying your referral link…"
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Role</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {(Object.keys(ROLE_INFO) as Role[]).map((roleKey) => (
                          <RoleOption key={roleKey} roleKey={roleKey} active={role === roleKey} onClick={handleRoleChange} />
                        ))}
                      </div>
                    </div>
                  )}

                  {role === "bl_advisor" && (
                    <div className="space-y-1.5">
                      <Label htmlFor="advisorCode">BL Advisor access code <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                        <Input
                          id="advisorCode"
                          placeholder="Enter access code"
                          value={advisorCode}
                          onChange={(event) => handleAdvisorCodeChange(event.target.value)}
                          className="pl-10 pr-24"
                          required
                          readOnly={advisorCodeVerified}
                        />
                        {advisorCodeVerified ? (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                            <CheckCircle2 className="h-4 w-4" />
                            Verified
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={handleVerifyAdvisorCode}
                            disabled={advisorCodeLoading || !advisorCode.trim()}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-gold-gradient px-2.5 py-1 text-xs font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
                          >
                            {advisorCodeLoading ? "Verifying…" : "Verify"}
                          </button>
                        )}
                      </div>
                      {advisorCodeError ? (
                        <p className="text-xs font-medium text-destructive">{advisorCodeError}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Required to create a BL Advisor account.</p>
                      )}
                    </div>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="firstName">First name <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                        <Input id="firstName" placeholder="John" value={firstName} onChange={(event) => setFirstName(event.target.value)} className="pl-10" required />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="lastName">Last name <span className="text-destructive">*</span></Label>
                      <div className="relative">
                        <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                        <Input id="lastName" placeholder="Doe" value={lastName} onChange={(event) => setLastName(event.target.value)} className="pl-10" required />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Phone <span className="text-xs text-muted-foreground">optional</span></Label>
                    <div className="relative">
                      <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                      <Input id="phone" type="tel" placeholder="+1 (555) 000-0000" value={phone} onChange={(event) => setPhone(event.target.value)} className="pl-10" />
                    </div>
                  </div>
                </>
              )}

              {!isLogin && <CaptchaField captcha={captcha} />}

              <div className="space-y-1.5">
                <Label htmlFor="email">Email{!isLogin && <span className="text-destructive"> *</span>}</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => handleEmailChange(event.target.value)}
                    className={`pl-10 ${!isLogin ? "pr-24" : ""}`}
                    required
                    readOnly={!isLogin && emailVerified}
                  />
                  {!isLogin && (
                    emailVerified ? (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                        <CheckCircle2 className="h-4 w-4" />
                        Verified
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={showOtpInput ? handleResendOtp : handleSendOtp}
                        disabled={otpLoading || captcha.incomplete || !isValidEmail(email) || (showOtpInput && otpResendCooldown > 0)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-gold-gradient px-2.5 py-1 text-xs font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
                      >
                        {otpLoading
                          ? "Sending…"
                          : showOtpInput && otpResendCooldown > 0
                          ? `Resend in ${otpResendCooldown}s`
                          : showOtpInput
                          ? "Resend"
                          : "Verify"}
                      </button>
                    )
                  )}
                </div>

                {/* Email already registered banner */}
                {!isLogin && emailAlreadyExists && (
                  <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                    <span className="mt-0.5 shrink-0 text-base">⚠️</span>
                    <span>
                      This email is already registered.{" "}
                      <button
                        type="button"
                        onClick={() => navigate(signInPath)}
                        className="font-semibold underline underline-offset-2 hover:text-amber-900"
                      >
                        Sign in instead
                      </button>
                      , or use a different email to create a new account.
                    </span>
                  </div>
                )}

                {/* Generic OTP send error */}
                {!isLogin && !showOtpInput && otpError && (
                  <p className="mt-1.5 text-xs font-medium text-destructive">{otpError}</p>
                )}

                {/* Inline OTP input — shown after "Verify" is clicked */}
                {!isLogin && showOtpInput && !emailVerified && (
                  <div className="mt-2 space-y-2">
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, "")); setOtpError(""); }}
                        className="text-center tracking-[0.3em] font-semibold"
                        autoFocus
                      />
                      <Button
                        type="button"
                        onClick={handleConfirmOtp}
                        disabled={otpLoading || otpCode.length !== 6}
                        className="shrink-0"
                      >
                        {otpLoading ? "…" : "Confirm"}
                      </Button>
                    </div>
                    {otpError && (
                      <p className="text-xs font-medium text-destructive">{otpError}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Password{!isLogin && <span className="text-destructive"> *</span>}</Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="pl-10 pr-11"
                    required
                    // Sign-in must accept legacy passwords shorter/weaker than the
                    // current rules — only NEW passwords (sign-up) are constrained.
                    minLength={isLogin ? undefined : PASSWORD_MIN_LENGTH}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {!isLogin && <PasswordRequirements password={password} className="pt-1" />}
              </div>

              {isLogin && (
                <div className="-mt-1 text-right">
                  <button
                    type="button"
                    onClick={() => { setFpStep("enter-email"); setFpEmail(email); setFpError(""); }}
                    className="text-xs font-medium text-primary underline underline-offset-2 hover:opacity-80"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {!isLogin && (
                <div
                  ref={consentRef}
                  className={`space-y-3 rounded-xl border bg-muted/25 p-3 transition-colors ${
                    consentHighlight ? "border-destructive ring-2 ring-destructive/40" : "border-border/75"
                  }`}
                >
                  <label className="flex items-start gap-2.5 text-sm text-foreground">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-border text-primary"
                      checked={consentAccepted}
                      onChange={(event) => {
                        setConsentAccepted(event.target.checked);
                        if (event.target.checked) setConsentHighlight(false);
                      }}
                    />
                    <span>
                      I have read and agree to the <a href="https://www.businesslocating.com/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline underline-offset-2">Terms and Conditions</a> and <a href="https://www.businesslocating.com/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline underline-offset-2">Privacy Policy</a>, and consent to proceed.
                    </span>
                  </label>
                </div>
              )}

              {/* Login solves it right above the button; registration solves it
                  above the email row, since the inline Verify (send-OTP) button
                  is gated too and every step reuses the one challenge. */}
              {isLogin && <CaptchaField captcha={captcha} />}

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={loading || captcha.incomplete || (!isLogin && (legalLoading || !legalDocs || !emailVerified || (role === "bl_advisor" && !advisorCodeVerified)))}
              >
                {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
                {!loading && <ArrowRight className="h-4 w-4" />}
              </Button>
              {!isLogin && !emailVerified && (
                <p className="text-center text-xs text-muted-foreground">Verify your email above to continue</p>
              )}
            </form>

            {/* ── Forgot-password modal overlay ── */}
            {isLogin && fpStep !== "idle" && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                <div className="w-full max-w-md rounded-2xl border border-border/80 bg-white p-6 shadow-xl space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-foreground">Reset your password</h3>
                    <button type="button" onClick={closeForgotPassword} className="text-muted-foreground hover:text-foreground">
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {fpStep === "enter-email" && (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">Enter your account email and we'll send a 6-digit reset code.</p>
                      <div className="space-y-1.5">
                        <Label htmlFor="fpEmail">Email address</Label>
                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                          <Input
                            id="fpEmail"
                            type="email"
                            placeholder="you@example.com"
                            value={fpEmail}
                            onChange={(e) => { setFpEmail(e.target.value); setFpError(""); }}
                            className="pl-10"
                            autoFocus
                          />
                        </div>
                      </div>
                      {fpError && <p className="text-xs font-medium text-destructive">{fpError}</p>}
                      <Button
                        type="button"
                        className="w-full rounded-full"
                        onClick={() => handleFpSendOtp(fpEmail)}
                        disabled={fpLoading || !fpEmail.trim()}
                      >
                        <Mail className="h-4 w-4" />
                        {fpLoading ? "Sending..." : "Send reset code"}
                      </Button>
                    </div>
                  )}

                  {fpStep === "enter-otp" && (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        A 6-digit code was sent to <strong>{fpEmail}</strong>. Enter it below.
                      </p>
                      <div className="space-y-1.5">
                        <Label htmlFor="fpOtp">6-digit code</Label>
                        <Input
                          id="fpOtp"
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="000000"
                          value={fpOtp}
                          onChange={(e) => { setFpOtp(e.target.value.replace(/\D/g, "")); setFpError(""); }}
                          className="text-center text-lg font-mono tracking-widest"
                          autoFocus
                        />
                      </div>
                      {fpError && <p className="text-xs font-medium text-destructive">{fpError}</p>}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          className="flex-1 rounded-full"
                          onClick={() => {
                            if (fpOtp.length !== 6) { setFpError("Enter the 6-digit code"); return; }
                            setFpError("");
                            setFpStep("enter-password");
                          }}
                          disabled={fpOtp.length !== 6}
                        >
                          Next
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => handleFpSendOtp(fpEmail)}
                          disabled={fpLoading || fpCooldown > 0}
                        >
                          {fpCooldown > 0 ? `Resend in ${fpCooldown}s` : "Resend"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {fpStep === "enter-password" && (
                    <form onSubmit={handleFpReset} className="space-y-4">
                      <p className="text-sm text-muted-foreground">Set your new password. It must meet all the requirements below.</p>
                      <div className="space-y-1.5">
                        <Label htmlFor="fpNewPw">New password</Label>
                        <div className="relative">
                          <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                          <Input
                            id="fpNewPw"
                            type={fpShowPassword ? "text" : "password"}
                            value={fpNewPassword}
                            onChange={(e) => { setFpNewPassword(e.target.value); setFpError(""); }}
                            className="pl-10 pr-10"
                            required
                            minLength={PASSWORD_MIN_LENGTH}
                            autoFocus
                          />
                          <button type="button" onClick={() => setFpShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                            {fpShowPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <PasswordRequirements password={fpNewPassword} className="pt-1" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="fpConfirmPw">Confirm new password</Label>
                        <div className="relative">
                          <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                          <Input
                            id="fpConfirmPw"
                            type="password"
                            value={fpConfirmPassword}
                            onChange={(e) => { setFpConfirmPassword(e.target.value); setFpError(""); }}
                            className="pl-10"
                            required
                            minLength={PASSWORD_MIN_LENGTH}
                          />
                        </div>
                        {fpConfirmPassword && fpNewPassword !== fpConfirmPassword && (
                          <p className="text-xs font-medium text-destructive">Passwords do not match.</p>
                        )}
                      </div>
                      {fpError && <p className="text-xs font-medium text-destructive">{fpError}</p>}
                      <Button type="submit" className="w-full rounded-full" disabled={fpLoading}>
                        <Lock className="h-4 w-4" />
                        {fpLoading ? "Resetting..." : "Reset password"}
                      </Button>
                    </form>
                  )}

                  {fpStep === "done" && (
                    <div className="space-y-4 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                        <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                      </div>
                      <p className="text-sm font-semibold text-foreground">Password reset successfully!</p>
                      <p className="text-xs text-muted-foreground">You can now sign in with your new password.</p>
                      <Button type="button" className="w-full rounded-full" onClick={closeForgotPassword}>
                        Back to Sign In
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <button onClick={() => navigate(isLogin ? registerPath : signInPath)} className="transition-colors hover:text-foreground">
                {isLogin ? (
                  <>Need an account? <span className="font-semibold text-primary underline underline-offset-2">Create one</span></>
                ) : (
                  <>Already have an account? <span className="font-semibold text-primary underline underline-offset-2">Sign in</span></>
                )}
              </button>
              <Link to={orgSlug ? `/${orgSlug}` : "/"} className="transition-colors hover:text-foreground">Return to home</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
